/** Imports */
import {z} from 'zod';
import {sql} from 'drizzle-orm';
import {zMongoObjectId} from '@/types/utils';
import {createSelectSchema} from 'drizzle-zod';
import {userChoiceFlags} from '@/modules/userChoiceFlags';
import {zOrganizationLanguageCodeSchema} from '@/mongoSchemas/Locale/LanguageCode.zod';
import {
    boolean,
    check,
    index,
    integer,
    pgTable,
    primaryKey,
    text,
    timestamp,
    varchar,
} from 'drizzle-orm/pg-core';


/** Constants */
export const DEFAULT_USER_THEME = 'light';
export const DEFAULT_AUTO_REDIRECT_EXTERNAL_LINKS = true;
export const zUserTheme = z.enum(['light', 'dark']);
export const zUserChoiceFlag = z.enum(
    Object.values(userChoiceFlags) as [UserChoiceFlag, ...Array<UserChoiceFlag>],
);
export const DEFAULT_USER_PREFERENCE_VALUES = {
    theme: DEFAULT_USER_THEME,
    preferredLanguage: null,
    autoRedirectExternalLinks: DEFAULT_AUTO_REDIRECT_EXTERNAL_LINKS,
    choiceFlags: [],
} satisfies UserPreferenceValues;


/** Drizzle schemas */
export const dUserPreferences = pgTable('user_preferences', {
    userMongoId: varchar('user_mongo_id', {length: 24})
        .primaryKey(),
    preferenceRevision: integer('preference_revision')
        .notNull()
        .default(0),
    theme: varchar('theme', {length: 5})
        .$type<UserTheme>()
        .notNull()
        .default(DEFAULT_USER_THEME),
    preferredLanguage: varchar('preferred_language', {length: 2})
        .$type<z.infer<typeof zOrganizationLanguageCodeSchema> | null>(),
    autoRedirectExternalLinks: boolean('auto_redirect_external_links')
        .notNull()
        .default(DEFAULT_AUTO_REDIRECT_EXTERNAL_LINKS),
    choiceFlags: text('choice_flags')
        .array()
        .$type<Array<UserChoiceFlag>>()
        .notNull()
        .default(sql`'{}'::text[]`),
    legacyBackfillCompletedAt: timestamp('legacy_backfill_completed_at', {mode: 'date', withTimezone: true}),
    createdAt: timestamp('created_at', {mode: 'date', withTimezone: true})
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', {mode: 'date', withTimezone: true})
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, (table) => [
    check('user_preferences_mongo_id_check', sql`${table.userMongoId} ~ '^[0-9a-f]{24}$'`),
    check('user_preferences_theme_check', sql`${table.theme} in ('light', 'dark')`),
    check(
        'user_preferences_language_check',
        sql`${table.preferredLanguage} is null or ${table.preferredLanguage} in ('EN', 'DE', 'IT', 'ES', 'FR')`,
    ),
]);

export const dUserFavoriteOrganizations = pgTable('user_favorite_organizations', {
    userMongoId: varchar('user_mongo_id', {length: 24})
        .notNull()
        .references(() => dUserPreferences.userMongoId, {onDelete: 'cascade'}),
    organizationMongoId: varchar('organization_mongo_id', {length: 24})
        .notNull(),
    createdAt: timestamp('created_at', {mode: 'date', withTimezone: true})
        .notNull()
        .defaultNow(),
}, (table) => [
    primaryKey({columns: [table.userMongoId, table.organizationMongoId]}),
    check('user_favorite_organizations_user_id_check', sql`${table.userMongoId} ~ '^[0-9a-f]{24}$'`),
    check(
        'user_favorite_organizations_organization_id_check',
        sql`${table.organizationMongoId} ~ '^[0-9a-f]{24}$'`,
    ),
    index('user_favorite_organizations_organization_id_idx')
        .on(table.organizationMongoId),
]);


/** Zod schemas */
export const zUserPreferenceValues = z.object({
    theme: zUserTheme,
    preferredLanguage: zOrganizationLanguageCodeSchema.nullable(),
    autoRedirectExternalLinks: z.boolean(),
    choiceFlags: z.array(zUserChoiceFlag),
});
export const zUserPreferences = createSelectSchema(dUserPreferences, {
    userMongoId: zMongoObjectId,
    ...zUserPreferenceValues.shape,
});
export const zUserFavoriteOrganization = createSelectSchema(dUserFavoriteOrganizations, {
    userMongoId: zMongoObjectId,
    organizationMongoId: zMongoObjectId,
});


/** Types */
export type UserTheme = z.infer<typeof zUserTheme>;
export type UserChoiceFlag = (typeof userChoiceFlags)[keyof typeof userChoiceFlags];
export type UserPreferences = z.infer<typeof zUserPreferences>;
export type UserPreferenceValues = z.infer<typeof zUserPreferenceValues>;
